import type { FC } from 'react';
import { memo, useEffect, useMemo, useState } from 'react';
import VideoPlayer, {
  type PlayerVars
} from '@components/VideoPlayer/component';
import type { KeenSliderOptions } from 'keen-slider/react';
import { useKeenSlider } from 'keen-slider/react';

import { LazyLoadImage } from '@components/reactLazyLoadImageComponent';
import Arrow from '@components/Carousel/components/Arrow';
import PPThumbs from '../PPThumbs';
import Lightbox from '@components/Lightbox';
import icons from '@icons';
import UIicons from '@ui-kit';
import { createImageErrorHandler } from '@utils/onImageError';
import { Picture } from '@ui';
import './index.scss';
import ProductImage18Plus from '@components/ProductImage18Plus';

const {
  maximize: { white: maxWhiteOutlined }
} = icons.Outline;
const { productCardErrorImg } = UIicons;
const onImageSrcErrorHandler = createImageErrorHandler(productCardErrorImg.x3);

const GALLERY_PLAYER_VARS: PlayerVars = {
  autoplay: 0,
  controls: 1,
  loop: 1,
  fs: 1
};

interface ProductPageMediaContainerProps {
  is_18plus: boolean;
  mediaArr: Array<{
    type: 'img' | 'video';
    original?: string;
    src: string;
    thumb: string;
  }>;
  title: string;
}

const ProductPageMediaContainer: FC<ProductPageMediaContainerProps> = ({
  mediaArr,
  title,
  is_18plus
}) => {
  const [isImageOpened, setIsImageOpened] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const sources = useMemo(
    () =>
      mediaArr
        .map(item => {
          if (item.type === 'img') {
            return item.original || item.src;
          }
          return null;
        })
        .filter(Boolean),
    [mediaArr]
  );

  const options: KeenSliderOptions = useMemo(
    () => ({
      rubberband: false,
      loop: true,
      slides: {
        spacing: 0,
        origin: 'center',
        perView: 1
      },
      slideChanged(slider) {
        setCurrentSlide(slider.track.details.rel);
      },
      created() {
        setLoaded(true);
      }
    }),
    []
  );

  const [sliderRef, instanceRef] = useKeenSlider(options);
  const isVideo = mediaArr[currentSlide]?.type === 'video';
  useEffect(() => {
    instanceRef?.current?.update(options);
  }, [sliderRef, mediaArr, instanceRef, options]);
  if (!mediaArr?.length) return null;
  const firstMediaImage = mediaArr?.find(item => item.type === 'img');

  return (
    <div className='relative max-w-[27.875rem] w-[27.875rem] lg:max-w-[27.875rem] lg:w-[27.875rem] flex md:max-w-full md:w-full'>
      <Lightbox
        type='image'
        toggler={isImageOpened}
        sourceIndex={currentSlide}
        sources={sources}
      />
      <PPThumbs
        images={mediaArr}
        is_18plus={is_18plus}
        changeSlide={instanceRef?.current?.moveToIdx}
        currentSlide={currentSlide}
        title={title}
      />
      <div className='relative pp-image-container'>
        {is_18plus ? (
          <ProductImage18Plus image={firstMediaImage?.src} />
        ) : (
          !!mediaArr?.length && (
            <div
              ref={sliderRef}
              className='keen-slider min-w-[23.75rem] max-w-[23.75rem] w-[23.75rem] lg:max-w-[23.75rem] lg:min-w-[23.75rem] lg:w-[23.75rem] '
            >
              {mediaArr.map((item, index) => (
                <div
                  key={`${item.src}-${index}`}
                  className={
                    'keen-slider__slide rounded-2xl h-[23.75rem] md:h-[30.625rem]'
                  }
                >
                  {item.type === 'img' && (
                    <div
                      onClick={() => {
                        setIsImageOpened(prev => !prev);
                      }}
                      className='absolute top-0 flex items-center justify-center w-1/3 h-full -translate-x-1/2 cursor-pointer left-1/2 hover:animate-blink'
                    >
                      <div className='pp-image-zoom opacity-0 cursor-pointer transition-opacity p-2 bg-[#303030]/[.3]  rounded-[50%]'>
                        <Picture
                          icon={maxWhiteOutlined}
                          width='24'
                          height='24'
                        />
                      </div>
                    </div>
                  )}
                  {item.type === 'img' ? (
                    <LazyLoadImage
                      visibleByDefault={index === 0}
                      draggable={false}
                      onError={onImageSrcErrorHandler}
                      className='object-contain w-full h-full'
                      src={item.src || productCardErrorImg.x3}
                      alt={`${title}-${index}}`}
                    />
                  ) : (
                    <VideoPlayer
                      key={item.src}
                      url={item.src}
                      playerVars={GALLERY_PLAYER_VARS}
                      width={380}
                      height={270}
                      isVideoPaused={index !== currentSlide}
                    />
                  )}
                </div>
              ))}
            </div>
          )
        )}
        {mediaArr.length > 1 && loaded && instanceRef && (
          <>
            <div
              onClick={e => {
                e.stopPropagation();
                instanceRef.current?.prev();
              }}
              className={`hover:animate-blink absolute z-10 left-0 top-1/2 -translate-y-1/2 w-1/3 flex items-center justify-center cursor-pointer select-none  ${
                isVideo ? 'h-[10.5rem]' : 'h-full'
              }`}
            >
              <Arrow
                className='pp-image-arrow bg-white rounded-[50%] p-1 opacity-0 allMd:opacity-100 transition-opacity'
                left
                height='26'
              />
            </div>
            <div
              onClick={e => {
                e.stopPropagation();
                instanceRef.current?.next();
              }}
              className={`hover:animate-blink absolute z-10 right-0 top-1/2 -translate-y-1/2 w-1/3 flex items-center justify-center cursor-pointer select-none ${
                isVideo ? 'h-[10.5rem]' : 'h-full'
              }`}
            >
              <Arrow
                className='pp-image-arrow bg-white rounded-[50%] p-1 opacity-0 allMd:opacity-100 transition-opacity'
                height='26'
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default memo(ProductPageMediaContainer);
